import {
  OrderBy,
  SessionStatus,
  TrainingPlanDetailsAttendeesModelActiveAlertTypesEnum,
  type HeadersValuesDTO,
  type TrainingPlanDetailsAttendeesModel,
  type TrainingPlanGroupTable,
  type TrainingPlanSessionsCounters,
} from "@simbelapp/order-sdk";
import { SessionFileTypeEnum } from "~/utils/enums/session-file-type.enums";
import { navTypeEnums, SessionAttendeesColumnEnum } from "~/utils/enums/sessions.enums";
import type { IFindOptions, IIndexHeader, IPaginationApi, ISdkFindParams } from "~/utils/interfaces/table-interfaces";
import type { ITrainerActivity, ITrainerActivityCounts } from "~/utils/interfaces/trainer-activities-interfaces";

/** Mirrors the backend's rule: only these types are listed to the trainer and accepted on import. */
export const TRAINER_VISIBLE_DOCUMENT_TYPES: SessionFileTypeEnum[] = [
  SessionFileTypeEnum.SIGN_IN_SHEET,
  SessionFileTypeEnum.TRAINING_SUPPORT,
  SessionFileTypeEnum.PROGRAM,
  SessionFileTypeEnum.OTHER,
  SessionFileTypeEnum.SATISFACTION_SURVEY,
];

/** The other alerts are the admins' concern. */
const TRAINER_VISIBLE_ALERT_TYPES: TrainingPlanDetailsAttendeesModelActiveAlertTypesEnum[] = [
  TrainingPlanDetailsAttendeesModelActiveAlertTypesEnum.Unavailability,
];

export const trainerAlertTypes = (
  types?: TrainingPlanDetailsAttendeesModelActiveAlertTypesEnum[] | null,
): TrainingPlanDetailsAttendeesModelActiveAlertTypesEnum[] =>
  (types ?? []).filter((type) => TRAINER_VISIBLE_ALERT_TYPES.includes(type));

/** Unlike the sensitive fields, alerts come unfiltered from the backend. */
export const withTrainerAlerts = (attendee: TrainingPlanDetailsAttendeesModel): TrainingPlanDetailsAttendeesModel => ({
  ...attendee,
  activeAlertTypes: trainerAlertTypes(attendee.activeAlertTypes),
});

const TRAINER_DEFAULT_ATTENDEE_COLUMNS = new Set<string>([
  SessionAttendeesColumnEnum.ALERTS,
  SessionAttendeesColumnEnum.LASTNAME,
  SessionAttendeesColumnEnum.EMAIL,
  SessionAttendeesColumnEnum.LEGAL_ENTITY,
  SessionAttendeesColumnEnum.JOB,
  SessionAttendeesColumnEnum.TEAM,
  SessionAttendeesColumnEnum.SITE,
  SessionAttendeesColumnEnum.STATUS,
  SessionAttendeesColumnEnum.ICS_STATUS,
  SessionAttendeesColumnEnum.COMPLETION_RATE,
  SessionAttendeesColumnEnum.ATTENDED_HOURS,
  SessionAttendeesColumnEnum.SCORE,
]);

/** Built-in columns only, which drops the custom fields and the legacy satisfaction columns. */
const TRAINER_ATTENDEE_COLUMNS = new Set<string>(Object.values(SessionAttendeesColumnEnum));

/** Never persist the result: grid state is per user and would overwrite this user's admin-page columns. */
export const applyTrainerAttendeeRestrictions = (headers: HeadersValuesDTO[] | IIndexHeader[]): IIndexHeader[] =>
  headers
    .filter((header) => TRAINER_ATTENDEE_COLUMNS.has(header.key ?? ""))
    .map((header) => ({ ...header, visible: TRAINER_DEFAULT_ATTENDEE_COLUMNS.has(header.key ?? "") }));

export const isTrainerOnTimeslot = (
  timeslot: { internalTrainers?: { userId: string }[] | null },
  userId?: string | null,
): boolean => !!userId && !!timeslot.internalTrainers?.some((trainer) => trainer.userId === userId);

export const activityStepsLabel = (count: number): string => `${count} étape${count > 1 ? "s" : ""}`;

/** The backend sends `nbAttendees: null` only to a user who is just a trainer on the session. */
export const isTrainerOnlyView = (item: { nbAttendees?: number | null } | null | undefined): boolean =>
  item?.nbAttendees === null;

const SESSION_STATUSES: string[] = Object.values(SessionStatus);

const toSessionStatus = (value?: string | null): SessionStatus | null =>
  value && SESSION_STATUSES.includes(value) ? (value as SessionStatus) : null;

export const toTrainerActivity = (row: TrainingPlanGroupTable): ITrainerActivity => ({
  id: row.id,
  trainingTitle: row.trainingTitle,
  trainingPartner: row.trainingPartner,
  tpLogo: row.tpLogo ?? null,
  startDate: row.startDate,
  endDate: row.endDate,
  sessionStatus: toSessionStatus(row.sessionStatus),
  nbTimeslots: row.nbTimeslots ?? 0,
  nbAttendees: row.nbAttendees,
});

export const EMPTY_TRAINER_ACTIVITY_COUNTS: ITrainerActivityCounts = { all: 0, coming: 0, inProgress: 0, finished: 0 };

export const toTrainerActivityCounts = (totalCount?: TrainingPlanSessionsCounters | null): ITrainerActivityCounts => ({
  all: totalCount?.total ?? 0,
  coming: totalCount?.coming ?? 0,
  inProgress: totalCount?.inProgress ?? 0,
  finished: totalCount?.finished ?? 0,
});

export const trainerActivityCountFor = (counts: ITrainerActivityCounts, filter: navTypeEnums): number => {
  switch (filter) {
    case navTypeEnums.COMING:
      return counts.coming;
    case navTypeEnums.IN_PROGRESS:
      return counts.inProgress;
    case navTypeEnums.FINISHED:
      return counts.finished;
    default:
      return counts.all;
  }
};

export const toSdkFindParams = ({ search, offset, limit, sortingColumn, order }: IFindOptions): ISdkFindParams => ({
  ...(search ? { search } : {}),
  ...(offset ? { offset } : {}),
  ...(limit ? { limit } : {}),
  ...(sortingColumn ? { sortingColumn } : {}),
  ...(order ? { order } : {}),
});

type SortValue = string | number | null | undefined;

const collator = new Intl.Collator("fr");

const isEmptyValue = (value: SortValue): boolean => value === null || value === undefined || value === "";

function sortRows<T>(
  rows: T[],
  key: string,
  order: string | null | undefined,
  sortValue: (row: T, key: string) => SortValue,
): T[] {
  const direction = order === OrderBy.Desc ? -1 : 1;
  return [...rows].sort((a, b) => {
    const left = sortValue(a, key);
    const right = sortValue(b, key);
    // Empty values stay last, whatever the sort direction.
    if (isEmptyValue(left) || isEmptyValue(right)) return Number(isEmptyValue(left)) - Number(isEmptyValue(right));
    const comparison =
      typeof left === "number" && typeof right === "number" ? left - right : collator.compare(`${left}`, `${right}`);
    return comparison * direction;
  });
}

/** Does a paginated route's search, sort and slicing on a list already in memory. */
export function queryRowsLocally<T>(
  rows: T[],
  options: IFindOptions,
  accessors: { matches?: (row: T, needle: string) => boolean; sortValue?: (row: T, key: string) => SortValue } = {},
): IPaginationApi<T> {
  const { matches, sortValue } = accessors;
  const needle = options.search?.trim().toLowerCase() ?? "";
  const filtered = needle && matches ? rows.filter((row) => matches(row, needle)) : rows;
  const key = options.sortingColumn;
  const sorted = key && sortValue ? sortRows(filtered, key, options.order, sortValue) : filtered;
  const offset = Number(options.offset ?? 0);
  const limit = Number(options.limit ?? sorted.length);

  return { data: sorted.slice(offset, offset + limit), meta: { count: sorted.length, totalCount: null } };
}
